/**
 * Practice Settings Page
 *
 * Page for managing organization settings including patient visibility
 * and templates configuration.
 * Part of the "Your Practice" feature.
 */

import { EPrescriptionOrgSection, useJotRxSetupState } from '@features/eprescriptions';
import { OrgTemplatesSection } from '@features/templates/components';
import { useCan } from '@features/user';
import { Box, Typography } from '@mui/material';
import { hasRole, ROLES } from '@utils/roleUtils';

import { OrganizationInfoCards } from './components/OrganizationInfoCards';
import { PatientVisibilitySection } from './components/PatientVisibilitySection';
import { PermissionsMatrix } from './components/PermissionsMatrix';
import { useOrganizationAccess } from './store';

export default function PracticeSettingsPage() {
  const { organizationData, userState } = useOrganizationAccess();
  const { isJotRxEnabled, isFullySetup } = useJotRxSetupState();
  // ADR 0073 Phase 3 (GDN-1590): catalog parity with hasAdminPrivileges
  // (is_admin_equivalent breadth) — see test_permissions.py BE parity tests.
  const isOrgAdmin = useCan('manage_organization');
  const isPracticeAdmin = hasRole(
    userState?.group_user_roles,
    ROLES.PRACTICE_ADMINISTRATOR,
  );

  // Show e-prescription section only when enabled and setup is incomplete
  const showEPrescriptionSetup = isJotRxEnabled && !isFullySetup;

  return (
    <>
      {/* E-Prescription Settings — above org info when setup is incomplete */}
      {showEPrescriptionSetup && (
        <Box id="eprescription-org-section" sx={{ mb: 3 }}>
          <EPrescriptionOrgSection />
        </Box>
      )}

      {/* Organization Info */}
      <OrganizationInfoCards
        organization={organizationData}
        activeMembers={[]}
        inactiveMembers={[]}
        variant="info"
      />

      {/* Patient Visibility Settings */}
      <Box sx={{ mb: 3 }}>
        <PatientVisibilitySection />
      </Box>

      {isPracticeAdmin && (
        <Box sx={{ mb: 3 }}>
          <PermissionsMatrix />
        </Box>
      )}

      {/* Resources Section (only for org admins) */}
      {isOrgAdmin && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Resources
          </Typography>
          <OrgTemplatesSection />
        </Box>
      )}
    </>
  );
}
